import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Translate, getTranslate } from 'react-localize-redux';
import { bindActionCreators } from 'redux';

import {
    withStyles, Typography, Button,
} from '@material-ui/core';

import * as ProgramStore from '../../../app/store/ProgramStore';

import urls from '../../../app/urls';
import apiUrls from '../../../youwell-common/apiUrls';
import { Image, HtmlContent } from '../../../youwell-common/components';
import { TrackedLink, ProgressBar } from '../../../common/components';
import MyTaskContent from '../../program/components/MyTaskContent';

const styles = theme => ({
    root: {
        background: theme.palette.grey[100],
        padding: theme.spacing(2),
        display: 'flex',
        flexWrap: 'wrap',

        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    topContainer: {
        flex: '0 0 100%',
    },
    title: {
        marginBottom: theme.spacing(2),
        fontWeight: 500,
    },
    imageContainer: {
        flex: '1 1 50%',

        [theme.breakpoints.down('sm')]: {
            alignSelf: 'center',
        },
    },
    content: {
        flex: '1 1 50%',
        paddingLeft: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',

        [theme.breakpoints.down('sm')]: {
            flex: '1 1 100%',
            paddingLeft: 0,
        },
    },
    image: {
        width: '100%',
    },
    moduleTitle: {
        margin: theme.spacing(1),
        fontSize: '1.14rem',
    },
    progressContainer: {
        margin: theme.spacing(1),
    },
    progress: {
        width: '100%',
    },
    description: {
        margin: theme.spacing(1),
    },
    actions: {
        flex: '1 1 auto',
        marginTop: theme.spacing(2),
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
    },
    startButton: {
        padding: theme.spacing(0.5, 2),

        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },

});

class CurrentModuleBox extends React.Component {
    renderModule = (module) => {
        const {
            classes, element, index, trackLabel, currentProgram, hideProgress, translate,
        } = this.props;

        const progress = Math.min(currentProgram.completedModules / currentProgram.totalModules, 1) || 0;

        return (
            <>
                <div className={classes.imageContainer}>
                    {module.moduleImageId && (
                        <Image
                            src={`${apiUrls.baseUrl}/${apiUrls.media.file}/${module.moduleImageId}`}
                            className={classes.image}
                            alt={translate('dashboard.moduleImageAltText', { n: index + 1 })}
                        />
                    )}
                </div>

                <div className={classes.content}>
                    <Typography variant="subtitle2" className={classes.moduleTitle}>
                        {element.name || (<Translate id="dashboard.module" data={{ index: index + 1 }} />)}
                    </Typography>

                    {!hideProgress && (
                        <div className={classes.progressContainer}>
                            <ProgressBar value={progress} className={classes.progress} />
                            <Typography variant="caption" className={classes.progressText}>
                                <Translate id="dashboard.xOfTotalModules" data={{ completed: currentProgram.completedModules, total: currentProgram.totalModules }} />
                            </Typography>
                        </div>
                    )}

                    <HtmlContent html={module.description} className={classes.description} />

                    <div className={classes.actions}>
                        <Button
                            variant="contained"
                            color={element.completed ? 'primary' : 'secondary'}
                            component={TrackedLink}
                            to={`${urls.inside.program}/${element.elementGroup}/${index}/${urls.inside.parts.resumeLatest}`}
                            className={classes.startButton}
                            size="small"
                            label={`${trackLabel}/${index}/${urls.inside.parts.resumeLatest}`}
                        >
                            {element.completed ? (<Translate id="dashboard.show" />) : (
                                <>
                                    {element.totalTasksVisited > 0 ? (
                                        <Translate id="dashboard.continue" />
                                    ) : (
                                        <Translate id="dashboard.start" />
                                    )}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </>
        );
    }

    completeTask = (elementId, taskId, programId) => () => {
        this.props.visitTask({
            ppeId: elementId,
            id: taskId,
        }, { reducerParams: { patientProgramId: programId, taskId } });
    }

    render() {
        const {
            classes, element, className, title, currentProgram,
        } = this.props;

        if (!element?.module && !element?.task) {
            return null;
        }

        return (
            <div className={classnames(classes.root, className)}>
                <div className={classes.topContainer}>
                    <Typography variant="h6" className={classes.title}>
                        {title}
                    </Typography>
                </div>
                {element.module
                    ? this.renderModule(element.module)
                    : (
                        <div>
                            <MyTaskContent
                                patientProgramElementId={element.id}
                                task={element.task}
                            />
                            <div className={classes.actions}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className={classes.startButton}
                                    size="small"
                                    onClick={this.completeTask(element.id, element.task.id, currentProgram.id)}
                                >
                                    <Translate id="dashboard.close" />
                                </Button>
                            </div>

                        </div>
                    )}
            </div>
        );
    }
}

CurrentModuleBox.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    visitTask: PropTypes.func.isRequired,
    element: PropTypes.object,
    // clientConfig: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    className: PropTypes.string,
    title: PropTypes.string,
    trackLabel: PropTypes.string.isRequired,
    currentProgram: PropTypes.object.isRequired,
    hideProgress: PropTypes.bool,
};

CurrentModuleBox.defaultProps = {
    className: null,
    title: null,
    element: null,
    hideProgress: false,
};


const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    clientConfig: state.auth.clientConfig,
    currentProgram: state.programStore.currentProgram,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(ProgramStore.actionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(CurrentModuleBox));
